<template>
  <RouterLink
    v-slot="{ href, navigate, isExactActive }"
    custom
    :to="to"
  >
    <div
      class="navigation-item"
      :class="{
        'q-ml-lg q-mt-sm': !($slots.prepend || icon) && !collapsedNavigationbarMenu,
        'navigation-item--active': isExactActive,
        'navigation-item--bordered': withBorder
      }"
    >
      <template v-if="!$slots.link">
        <component
          :is="disabled ? 'div' : 'a'"
          class="navigation-item__link row no-wrap items-center full-width"
          :class="{
            'q-px-md q-py-sm': withPadding
          }"
          :href="href"
          @click.stop="!disabled && navigate($event)"
        >
          <slot
            v-if="$slots.prepend"
            name="prepend"
          />

          <template v-else-if="icon">
            <Transition
              mode="out-in"
              :name="noIconTransition ? '' : 'icon'"
            >
              <rv-action-button
                v-if="collapsedNavigationbar"
                class="q-ml-md"
                :data-automation="`navigation-bar-link__${String(to.name)}__button`"
                :icon="icon"
                :selected="isExactActive"
              >
                <slot
                  v-if="$slots['button-content']"
                  name="button-content"
                />
              </rv-action-button>
              <div
                v-else
                class="q-my-sm q-ml-lg q-mr-md"
              >
                <q-icon
                  class="navigation-item__icon"
                  :name="icon"
                  size="1.5rem"
                />
              </div>
            </Transition>
            <Transition name="text">
              <div
                v-if="!hideLabel && !collapsedNavigationbar"
                class="row full-width justify-between items-center"
              >
                <rv-ellipsis-text
                  class="text-subtitle3"
                  :data-automation="`navigation-bar-link__${String(to.name)}__label`"
                  :text="label"
                />
              </div>
            </Transition>
          </template>

          <Transition name="text">
            <rv-ellipsis-text
              v-if="!icon && !hideLabel && !collapsedNavigationbar"
              :class="{ 'navigation-item__link__label q-ml-md text-subtitle3 col' : !collapsedNavigationbarMenu }"
              :data-automation="`navigation-bar-link__${String(to.name)}__label`"
              :text="label"
            />
          </Transition>

          <q-icon
            class="q-ml-sm"
            :color="appendedIconColor"
            :name="appendedIcon"
            size="1.5rem"
          />

          <slot
            v-if="$slots.append"
            name="append"
          />
        </component>
      </template>
      <slot
        v-else
        :collapsed="collapsedNavigationbar"
        :href="href"
        :icon="icon"
        :label="label"
        name="link"
        :navigate="navigate"
        :to="to"
      />
    </div>
  </RouterLink>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import type { LocationAsRelativeRaw } from 'vue-router'
import { RouterLink } from 'vue-router'


defineProps({
  appendedIcon: {
    default: '',
    type: String as PropType<string>,
  },
  appendedIconColor: {
    default: undefined,
    type: String as PropType<string>,
  },
  collapsedAfterAnimation: {
    default: false,
    type: Boolean as PropType<boolean>,
  },
  collapsedNavigationbar: {
    default: false,
    type: Boolean as PropType<boolean>,
  },
  collapsedNavigationbarMenu: {
    default: false,
    type: Boolean as PropType<boolean>,
  },
  disabled: {
    default: false,
    type: Boolean as PropType<boolean>,
  },
  hideLabel: {
    default: false,
    type: Boolean as PropType<boolean>,
  },
  icon: {
    default: undefined,
    type: String as PropType<string>,
  },
  label: {
    required: true,
    type: String as PropType<string>,
  },
  noIconTransition: {
    default: false,
    type: Boolean as PropType<boolean>,
  },
  to: {
    required: true,
    type: Object as PropType<LocationAsRelativeRaw>,
  },
  withBorder: {
    default: false,
    type: Boolean as PropType<boolean>,
  },
  withPadding: {
    default: false,
    type: Boolean as PropType<boolean>,
  },
})
</script>

<style lang="sass" scoped>
@import '../css/quasar.variables'


.navigation-item
  &__link
    --text-color: var(--q-primary-fixed)

    color: var(--q-black-1) !important
    z-index: 1

    &__label
      line-height: 32px

    &:hover
      color: var(--text-color) !important
      &__icon
        color: var(--text-color) !important

      :deep(.q-img)
        border: 1px solid var(--q-primary)

        .q-img__image
          border-radius: 0px

    &--active
      color: var(--q-primary) !important

    &--bordered
      border-top: 1px solid rgba(0, 0, 0, .12)

    :deep(.q-btn)
      height: 40px !important
      width: 40px !important

  &--active
    a
      color: var(--text-color) !important

    :deep(.q-img)
      border: 1px solid var(--q-primary)

      .q-img__image
        border-radius: 0px
</style>
